import { useEffect, useState, useCallback } from "react";
import { api, mesISOLocal } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { History, Download, TrendingUp, RefreshCw, Users2, PiggyBank, ArrowDownCircle } from "lucide-react";
import KpiCard from "@/components/KpiCard";
import UsageTip from "@/components/UsageTip";
import ExcelExportButton from "@/components/ExcelExportButton";

const cop = (v) => `$ ${(Number(v) || 0).toLocaleString("es-CO")}`;
const mesLabel = (m) => {
  if (!m) return "";
  const [y, mm] = m.split("-");
  const nombres = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${nombres[parseInt(mm, 10) - 1]} ${y}`;
};

export default function HistorialSociosPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const hoy = new Date();
  const hastaDefault = mesISOLocal(hoy);
  const desdeDefault = (() => {
    const d = new Date(hoy);
    d.setMonth(d.getMonth() - 11);
    return mesISOLocal(d);
  })();
  const [desde, setDesde] = useState(desdeDefault);
  const [hasta, setHasta] = useState(hastaDefault);
  const [socioFiltro, setSocioFiltro] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ desde, hasta });
      if (socioFiltro) params.set("socio_id", socioFiltro);
      const { data } = await api.get(`/socios/historial?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el historial");
    } finally { setLoading(false); }
  }, [desde, hasta, socioFiltro]);

  useEffect(() => { load(); }, [load]);

  const exportar = async () => {
    try {
      const params = new URLSearchParams({ desde, hasta });
      const res = await api.get(`/socios/historial/exportar-excel?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Historial-Socios-${desde}_a_${hasta}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) { toast.error("Error exportando"); }
  };

  if (loading && !data) return <div className="p-6 text-muted-foreground">Cargando historial...</div>;
  if (!data) return <div className="p-6 text-muted-foreground">Sin datos.</div>;

  const { kpis, timeline, capital_socios } = data;
  const maxDist = Math.max(1, ...timeline.map((t) => t.distribucion_total));

  return (
    <div>
      <UsageTip
        title="Cómo leer el historial"
        steps={[
          "<b>Distribuido</b>: cuánto se repartió del negocio a los socios ese mes.",
          "<b>Retirado</b>: cuánto sacaron los socios efectivamente.",
          "<b>Reinvertido</b>: lo que quedó sin retirar y se sumó al capital (fin de mes).",
          "El % de reinversión te dice qué tan agresivo está creciendo el negocio con plata de los socios.",
        ]}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="hist-kpis">
        <KPI icon={TrendingUp} label="Distribuido histórico" to="/finanzas?tab=socios" value={cop(kpis.total_distribuido)} tone="primary" testid="kpi-dist" />
        <KPI icon={ArrowDownCircle} label="Retirado histórico" value={cop(kpis.total_retirado)} tone="rose" testid="kpi-ret" />
        <KPI icon={RefreshCw} label="Reinvertido histórico" value={cop(kpis.total_reinvertido)} tone="emerald" testid="kpi-reinv" />
        <KPI icon={PiggyBank} label="Capital consolidado" to="/finanzas?tab=socios" value={cop(kpis.capital_consolidado)} tone="blue" testid="kpi-cap" />
        <KPI icon={Users2} label="Socios activos" to="/finanzas?tab=socios" value={kpis.socios_activos} tone="muted" testid="kpi-socios" />
      </div>

      {/* Filtros */}
      <div className="bg-card border border-border rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-end" data-testid="hist-filtros">
        <div>
          <Label className="text-xs">Desde</Label>
          <Input type="month" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 mt-1 w-40" data-testid="hist-desde" />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input type="month" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 mt-1 w-40" data-testid="hist-hasta" />
        </div>
        <div>
          <Label className="text-xs">Socio (opcional)</Label>
          <select value={socioFiltro} onChange={(e) => setSocioFiltro(e.target.value)}
            className="h-9 mt-1 w-52 border border-border rounded-md px-2 bg-card text-sm" data-testid="hist-socio">
            <option value="">Todos</option>
            {capital_socios.map((s) => <option key={s.socio_id} value={s.socio_id}>{s.nombre}</option>)}
          </select>
        </div>
        <ExcelExportButton
          endpoint="/socios/historial/exportar-excel"
          filename="Historial-Socios"
          label="Exportar Excel"
          extraParams={socioFiltro ? { socio_id: socioFiltro } : {}}
          className="h-9"
          testid="hist-export"
        />
      </div>

      {/* Timeline mensual */}
      <div className="bg-card border border-border rounded-xl overflow-hidden mb-6" data-testid="hist-timeline">
        <div className="px-4 py-3 border-b border-border/60">
          <div className="text-sm font-semibold flex items-center gap-2"><History className="w-4 h-4 text-primary" /> Timeline mensual</div>
          <div className="text-xs text-muted-foreground">Distribuido · Retirado · Reinvertido, con % de reinversión.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/40">
              <tr>
                <th className="px-4 py-2">Mes</th>
                <th className="px-4 py-2 text-right">Distribuido</th>
                <th className="px-4 py-2 text-right">Retirado</th>
                <th className="px-4 py-2 text-right">Reinvertido</th>
                <th className="px-4 py-2">% Reinv.</th>
                <th className="px-4 py-2 text-right">Aportes extra</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {timeline.filter((t) => t.distribucion_total || t.retiros_total || t.reinvertido || t.aportes_extra).length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground italic">Sin movimientos en el rango seleccionado.</td></tr>
              ) : timeline.map((row) => {
                const hasMov = row.distribucion_total || row.retiros_total || row.reinvertido || row.aportes_extra;
                if (!hasMov) return null;
                const bar = Math.round((row.distribucion_total / maxDist) * 100);
                return (
                  <tr key={row.mes} className="hover:bg-accent/30" data-testid={`hist-mes-${row.mes}`}>
                    <td className="px-4 py-2 font-mono text-xs">{mesLabel(row.mes)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold text-primary">{cop(row.distribucion_total)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-rose-700">{cop(row.retiros_total)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{cop(row.reinvertido)}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${row.pct_reinvertido}%` }}></div>
                        </div>
                        <span className="text-xs tabular-nums w-10">{row.pct_reinvertido}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-blue-700">{cop(row.aportes_extra)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Capital por socio */}
      <div className="bg-card border border-border rounded-xl overflow-hidden mb-6" data-testid="hist-capital">
        <div className="px-4 py-3 border-b border-border/60">
          <div className="text-sm font-semibold flex items-center gap-2"><PiggyBank className="w-4 h-4 text-primary" /> Capital consolidado por socio</div>
          <div className="text-xs text-muted-foreground">Suma histórica: aporte + aportes extra + reinvertido − retirado.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/40">
              <tr>
                <th className="px-4 py-2">Socio</th>
                <th className="px-4 py-2 text-right">%</th>
                <th className="px-4 py-2 text-right">Aporte inicial</th>
                <th className="px-4 py-2 text-right">Aportes extra</th>
                <th className="px-4 py-2 text-right">Reinvertido</th>
                <th className="px-4 py-2 text-right">Retirado</th>
                <th className="px-4 py-2 text-right">Distribuido total</th>
                <th className="px-4 py-2 text-right">Capital actual</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {capital_socios.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground italic">Sin socios registrados.</td></tr>
              ) : capital_socios.map((s) => (
                <tr key={s.socio_id} className={`hover:bg-accent/30 ${!s.activo ? "opacity-60" : ""}`} data-testid={`hist-socio-${s.socio_id}`}>
                  <td className="px-4 py-2 font-semibold">
                    {s.nombre}
                    {!s.activo && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-bold uppercase">Inactivo</span>}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{s.porcentaje}%</td>
                  <td className="px-4 py-2 text-right tabular-nums">{cop(s.aporte_inicial)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-blue-700">{cop(s.aportes_extra)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{cop(s.reinvertido)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-rose-700">−{cop(s.retirado)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{cop(s.distribuido_total)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-primary">{cop(s.capital_actual)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Retiros cruzados por mes × socio */}
      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="hist-retiros">
        <div className="px-4 py-3 border-b border-border/60">
          <div className="text-sm font-semibold flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-primary" /> Retiros cruzados por socio</div>
          <div className="text-xs text-muted-foreground">Quién sacó cuánto, mes por mes.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/40">
              <tr>
                <th className="px-4 py-2">Mes</th>
                {capital_socios.map((s) => <th key={s.socio_id} className="px-4 py-2 text-right">{s.nombre}</th>)}
                <th className="px-4 py-2 text-right font-bold">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {timeline.filter((t) => t.retiros_total > 0).length === 0 ? (
                <tr><td colSpan={capital_socios.length + 2} className="px-4 py-8 text-center text-muted-foreground italic">Sin retiros en el rango.</td></tr>
              ) : timeline.filter((t) => t.retiros_total > 0).map((row) => {
                const bySocio = Object.fromEntries((row.por_socio || []).map((s) => [s.socio_id, s.retiro]));
                return (
                  <tr key={row.mes} className="hover:bg-accent/30">
                    <td className="px-4 py-2 font-mono text-xs">{mesLabel(row.mes)}</td>
                    {capital_socios.map((s) => (
                      <td key={s.socio_id} className="px-4 py-2 text-right tabular-nums text-rose-700">
                        {bySocio[s.socio_id] ? `−${cop(bySocio[s.socio_id])}` : "—"}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right tabular-nums font-bold text-rose-700">−{cop(row.retiros_total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KPI(props) { return <KpiCard {...props} />; }
